import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { C } from '../styles/common';

// Admin → Measuring → Routines (the director, 7 Oct and 3 Nov 2026): a process
// recorded as laps. "Tap, tap, tap, name them": the first recording writes the
// steps, you edit and confirm them, and from then on they are the buttons on
// the phone. Every confirmed change is a new version, in History, and a
// recording keeps the version it was taken with, so old ones mean what they meant.

const TEAL = C.teal, AMBER = '#f59e0b', GREEN = '#34d399', DIM = '#9ca3af', SUNK = '#12161b', VIOLET = '#a78bfa';
const STATUS = { draft: ['Draft', AMBER], confirmed: ['Confirmed', GREEN], retired: ['Retired', DIM] };
const REC = { counted: ['Counted', GREEN], set_aside: ['Set aside', AMBER], abandoned: ['Thrown away', DIM] };
const day = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : '');
const secs = (s) => (s == null ? '' : s >= 60 ? `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')} min` : `${Math.round(s * 10) / 10} s`);
const todayIso = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Ljubljana' });
const BELONGS = [['', 'Nothing in particular'], ['machine', 'A machine'], ['part', 'A part'], ['product', 'A product'], ['mould', 'A mould']];
const LISTS = { machine: '/api/v1/machines', part: '/api/v1/parts', product: '/api/v1/measuring/subjects', mould: '/api/v1/moulds' };

export default function Routines() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState(null);
  const [adding, setAdding] = useState(false);
  const [people, setPeople] = useState([]);
  const [wide, setWide] = useState(() => window.innerWidth >= 900);

  const load = useCallback(() => api.get('/api/v1/measuring/routines')
    .then(r => { setData(r.data); return r.data; })
    .catch(err => { setError(err.response?.data?.error || 'The routines could not be read.'); return null; }), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/api/v1/measuring/plan').then(r => setPeople(r.data.people || [])).catch(() => {}); }, []);
  useEffect(() => {
    const on = () => setWide(window.innerWidth >= 900);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);

  async function act(fn) {
    setBusy(true); setError('');
    let ok = true;
    try { await fn(); } catch (err) { ok = false; setError(err.response?.data?.error || 'That did not work. Try again.'); }
    await load();
    setBusy(false);
    return ok;
  }

  if (!data) return error ? <Note>{error}</Note> : <div style={{ color: C.muted }}>Loading…</div>;
  const list = data.routines || [];
  const live = list.filter(r => r.status !== 'retired'), retired = list.filter(r => r.status === 'retired');
  const current = list.find(r => r.id === sel) || (wide ? live[0] : null);

  const detail = current && <Detail key={current.id} r={current} busy={busy} people={people} reasons={data.set_aside_reasons || []} act={act} />;
  const row = (r) => {
    const [label, tone] = STATUS[r.status];
    const on = current && current.id === r.id;
    return (
      <div key={r.id}>
        <button onClick={() => setSel(on && !wide ? null : r.id)} aria-current={on ? 'true' : undefined}
          style={{ ...card, width: '100%', textAlign: 'left', cursor: 'pointer', color: C.text, borderColor: on ? TEAL : C.border, background: on ? 'rgba(20,184,166,0.07)' : C.surface, padding: '10px 12px' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <b style={{ fontSize: 13.5, flex: 1, minWidth: 0 }}>{r.name}</b>
            <span style={{ fontSize: 11, fontWeight: 700, color: tone, border: `1px solid ${tone}`, borderRadius: 99, padding: '2px 8px', whiteSpace: 'nowrap' }}>{label}{r.version > 0 ? ` · v${r.version}` : ''}</span>
          </div>
          <div style={{ fontSize: 12, color: DIM, marginTop: 3 }}>
            {r.attached_name ? `${r.attached_name} · ` : ''}{r.steps.length ? `${r.steps.length} step${r.steps.length === 1 ? '' : 's'}` : 'No steps yet'} · {r.counted} recording{r.counted === 1 ? '' : 's'}
            {r.total_person_s ? ` · about ${secs(r.total_person_s)} each` : ''}
          </div>
        </button>
        {!wide && on && <div style={{ marginTop: 6 }}>{detail}</div>}
      </div>
    );
  };

  return (
    <>
      {error && <Note>{error}</Note>}
      <div style={{ marginBottom: 12 }}>
        {adding
          ? <NewRoutine busy={busy} onCancel={() => setAdding(false)} onSave={async (body) => { const ok = await act(() => api.post('/api/v1/measuring/routines', body)); if (ok) setAdding(false); }} />
          : <button onClick={() => setAdding(true)} style={{ ...btn, ...teal }}>+ New routine</button>}
      </div>
      {!list.length && !adding && (
        <div style={{ ...card, color: DIM, fontSize: 14, lineHeight: 1.55 }}>
          <b style={{ color: C.text }}>No routines yet.</b> Add one, ask someone to record it (or record it yourself), and name the laps at the end. Good ones to start with are things done the same way every time: changing a filter, setting up a mould's water, a daily machine check.
        </div>
      )}
      {list.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: wide ? 'minmax(0, 4fr) minmax(0, 8fr)' : '1fr', gap: 14, alignItems: 'start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {live.map(row)}
            {retired.length > 0 && <div style={{ ...sect, marginTop: 10 }}>Retired</div>}
            {retired.map(row)}
          </div>
          {wide && <div style={{ position: 'sticky', top: 12 }}>{detail || <div style={{ ...card, color: DIM }}>Choose a routine on the left.</div>}</div>}
        </div>
      )}
    </>
  );
}

function NewRoutine({ busy, onSave, onCancel }) {
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [id, setId] = useState('');
  const [options, setOptions] = useState(null);
  useEffect(() => {
    if (!type) { setOptions(null); return; }
    setOptions(null); setId('');
    api.get(LISTS[type]).then(r => {
      const raw = type === 'product' ? r.data.products : (Array.isArray(r.data) ? r.data : r.data[`${type}s`] || []);
      setOptions(raw.map(x => ({ v: String(x.id), l: x.display_name || x.name || x.ref })));
    }).catch(() => setOptions([]));
  }, [type]);
  const ready = name.trim() && (!type || id);
  return (
    <div style={{ background: SUNK, border: `1px solid ${TEAL}`, borderRadius: 12, padding: 14 }}>
      <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 10 }}>New routine</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '10px 14px' }}>
        <Field label="What is it called? In the words people use" id="rt-name"><input id="rt-name" value={name} onChange={e => setName(e.target.value)} placeholder="Changing the hopper filter" style={{ ...select, width: '100%', boxSizing: 'border-box' }} /></Field>
        <Field label="It belongs to" id="rt-type">
          <select id="rt-type" value={type} onChange={e => setType(e.target.value)} style={{ ...select, width: '100%' }}>{BELONGS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        </Field>
        {type && (
          <Field label={`Which ${type}`} id="rt-id">
            {options === null ? <span style={{ fontSize: 12, color: C.muted }}>Reading…</span> : (
              <select id="rt-id" value={id} onChange={e => setId(e.target.value)} style={{ ...select, width: '100%' }}>
                <option value="">Choose…</option>
                {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            )}
          </Field>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button disabled={busy || !ready} onClick={() => onSave({ name, attached_type: type || null, attached_id: id || null })} style={{ ...btn, ...teal, opacity: ready ? 1 : 0.5 }}>Add it</button>
        <button onClick={onCancel} style={{ ...btn, background: 'none', color: DIM }}>Cancel</button>
      </div>
    </div>
  );
}

function Detail({ r, busy, people, reasons, act }) {
  const start = r.steps.length ? r.steps : r.suggested || [];
  const [steps, setSteps] = useState(() => start.map(s => s.name));
  const [who, setWho] = useState('');
  const [when, setWhen] = useState(todayIso);
  const [note, setNote] = useState('');
  const changed = JSON.stringify(steps) !== JSON.stringify(r.steps.map(s => s.name));
  const body = () => ({ steps: steps.map(name => ({ name })) });
  const at = (i, fn) => setSteps(list => fn(list.slice(), i));
  const [label, tone] = STATUS[r.status];
  return (
    <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.muted }}>Routine{r.attached_name ? ` · ${r.attached_name}` : ''}</div>
        <h2 style={{ margin: '2px 0', fontSize: 19 }}>{r.name}</h2>
        <div style={{ fontSize: 13, color: tone }}>{label}{r.version > 0 ? `, version ${r.version}` : ''}{r.confirmed_by ? <span style={{ color: DIM }}> · confirmed by {r.confirmed_by} {day(r.confirmed_at)}</span> : ''}</div>
      </div>

      {/* The steps */}
      <div>
        <div style={sect}>Steps{r.status === 'confirmed' ? ' · the buttons on the phone' : ''}</div>
        {r.status === 'draft' && r.suggested && !r.steps.length && <div style={{ fontSize: 13, color: DIM, marginBottom: 8 }}>These come from the latest recording. Change the words, join or add steps, then confirm them.</div>}
        {r.status === 'draft' && !r.suggested && !r.steps.length && <div style={{ fontSize: 13, color: DIM, marginBottom: 8 }}>Nothing recorded yet. Ask for a recording below and the steps come from it, or type them here if you know them.</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {steps.map((name, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12, color: C.muted, width: 20, textAlign: 'right' }}>{i + 1}</span>
              <input aria-label={`Step ${i + 1}`} value={name} onChange={e => { const v = e.target.value; at(i, (l) => { l[i] = v; return l; }); }} style={{ ...select, flex: 1, minWidth: 0, padding: '6px 8px' }} />
              <button aria-label={`Move step ${i + 1} up`} disabled={!i} onClick={() => at(i, (l) => { [l[i - 1], l[i]] = [l[i], l[i - 1]]; return l; })} style={{ ...mini, opacity: i ? 1 : 0.35 }}>▲</button>
              <button aria-label={`Move step ${i + 1} down`} disabled={i === steps.length - 1} onClick={() => at(i, (l) => { [l[i + 1], l[i]] = [l[i], l[i + 1]]; return l; })} style={{ ...mini, opacity: i === steps.length - 1 ? 0.35 : 1 }}>▼</button>
              <button title="Join with the next step" aria-label={`Join step ${i + 1} with the next`} disabled={i === steps.length - 1} onClick={() => at(i, (l) => { l.splice(i + 1, 1); return l; })} style={{ ...mini, opacity: i === steps.length - 1 ? 0.35 : 1 }}>⤓</button>
              <button aria-label={`Remove step ${i + 1}`} onClick={() => at(i, (l) => { l.splice(i, 1); return l; })} style={mini}>✕</button>
            </div>
          ))}
          <div><button onClick={() => setSteps(l => [...l, ''])} style={{ ...btn, background: 'none' }}>+ A step</button></div>
        </div>
        {r.status !== 'retired' && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
            {r.status === 'draft' && <button disabled={busy || !steps.length} onClick={() => act(() => api.put(`/api/v1/measuring/routines/${r.id}`, { ...body(), confirm: true }))} style={{ ...btn, ...teal, opacity: steps.length ? 1 : 0.5 }}>Confirm these steps</button>}
            {r.status === 'draft' && changed && <button disabled={busy} onClick={() => act(() => api.put(`/api/v1/measuring/routines/${r.id}`, body()))} style={btn}>Save, not confirmed yet</button>}
            {r.status === 'confirmed' && changed && <button disabled={busy} onClick={() => act(() => api.put(`/api/v1/measuring/routines/${r.id}`, body()))} style={{ ...btn, ...teal }}>Save as version {r.version + 1}</button>}
          </div>
        )}
        {r.status === 'confirmed' && changed && <div style={{ fontSize: 12.5, color: DIM, marginTop: 6 }}>The buttons change from the next recording. Recordings already taken keep version {r.version}.</div>}
      </div>

      {/* Where the time goes */}
      {r.step_times.length > 0 && (
        <div>
          <div style={sect}>Where the time goes · {r.this_version} recording{r.this_version === 1 ? '' : 's'}{r.version ? ` of version ${r.version}` : ''}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {r.step_times.map(s => (
              <div key={s.name} style={{ fontSize: 13 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                  <span>{s.name}</span>
                  <span style={{ fontFamily: 'ui-monospace, monospace' }}>{secs(s.mid)}<span style={{ color: C.muted }}> · {s.share}%</span></span>
                </div>
                <div style={{ height: 6, borderRadius: 3, background: SUNK, marginTop: 3, overflow: 'hidden' }}><div style={{ height: '100%', width: `${s.share}%`, background: TEAL }} /></div>
                {s.by_person.length > 1 && <div style={{ fontSize: 12, color: DIM, marginTop: 2 }}>{s.by_person.map(p => `${p.who} ${secs(p.mid)}`).join(' · ')}</div>}
              </div>
            ))}
          </div>
          {r.total_person_s && <div style={{ fontSize: 13, color: DIM, marginTop: 8 }}>About <b style={{ color: C.text }}>{secs(r.total_person_s)}</b> of person-time each, the steps' middles added up.</div>}
        </div>
      )}

      {/* Asking for a recording */}
      {r.status !== 'retired' && (
        <div style={{ background: SUNK, border: `1px solid ${C.border}`, borderRadius: 10, padding: '11px 13px' }}>
          <div style={{ fontWeight: 700, fontSize: 13.5, marginBottom: 8 }}>Ask for a recording</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '8px 12px' }}>
            <Field label="Who records it" id={`rt-who-${r.id}`}>
              <select id={`rt-who-${r.id}`} value={who} onChange={e => setWho(e.target.value)} style={{ ...select, width: '100%' }}>
                <option value="">Choose…</option>
                {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <Field label="Day" id={`rt-day-${r.id}`}><input id={`rt-day-${r.id}`} type="date" value={when} min={todayIso()} onChange={e => setWhen(e.target.value)} style={{ ...select, width: '100%', boxSizing: 'border-box' }} /></Field>
            <Field label="Why · they see this on their phone" id={`rt-why-${r.id}`}><input id={`rt-why-${r.id}`} value={note} onChange={e => setNote(e.target.value)} placeholder="Optional" style={{ ...select, width: '100%', boxSizing: 'border-box' }} /></Field>
          </div>
          <button disabled={busy || !who || !when} onClick={async () => { const ok = await act(() => api.post('/api/v1/measuring/tasks', { day: when, kind: 'routine', routine_id: r.id, user_id: who, note })); if (ok) { setWho(''); setNote(''); } }}
            style={{ ...btn, ...teal, marginTop: 10, opacity: who && when ? 1 : 0.5 }}>Ask{who ? ` ${(people.find(p => p.id === who) || {}).name}` : ''}</button>
          <div style={{ fontSize: 12, color: DIM, marginTop: 6 }}>It shows on their Record button that day. To record it yourself, choose your own name.</div>
        </div>
      )}

      {/* The recordings */}
      <div>
        <div style={sect}>Recordings · never edited or deleted</div>
        {!r.recordings.length && <div style={{ fontSize: 13, color: DIM }}>None yet.</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {r.recordings.map(m => <Recording key={m.id} m={m} busy={busy} reasons={reasons} act={act} />)}
        </div>
      </div>

      <div>
        {r.status === 'retired'
          ? <button disabled={busy} onClick={() => act(() => api.post(`/api/v1/measuring/routines/${r.id}/retire`, { retired: false }))} style={btn}>Bring it back</button>
          : <button disabled={busy} onClick={() => act(() => api.post(`/api/v1/measuring/routines/${r.id}/retire`, { retired: true }))} style={{ ...btn, background: 'none', color: DIM }}>Retire it</button>}
      </div>
    </div>
  );
}

function Recording({ m, busy, reasons, act }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [label, tone] = REC[m.status] || [m.status, DIM];
  const ready = reason && (reason !== 'Other' || note.trim().length >= 3);
  return (
    <div style={{ background: SUNK, border: `1px solid ${C.border}`, borderRadius: 9, padding: '8px 10px', fontSize: 13 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px', alignItems: 'baseline' }}>
        <span style={{ color: DIM }}>{day(m.started_at)} · {m.who || 'someone'}</span>
        {m.value != null && <b style={{ fontFamily: 'ui-monospace, monospace', color: m.status === 'counted' ? C.text : DIM }}>{secs(m.value)} each</b>}
        {m.version > 0 && <span style={{ color: VIOLET, fontSize: 12 }}>v{m.version}</span>}
        <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: tone, border: `1px solid ${tone}`, borderRadius: 99, padding: '1px 7px' }}>{label}</span>
      </div>
      {m.steps.length > 0 && <div style={{ fontSize: 12, color: DIM, marginTop: 3 }}>{m.steps.map(s => `${s.name} ${secs(s.person_s)}`).join(' · ')}</div>}
      {m.status === 'set_aside' && <div style={{ fontSize: 12, color: AMBER, marginTop: 3 }}>Set aside: {m.set_aside_reason}</div>}
      {m.status === 'abandoned' && <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>Thrown away on the floor{m.abandon_reason ? `: ${m.abandon_reason}` : ''}. Never counted.</div>}
      <div style={{ marginTop: 4 }}>
        {m.status === 'counted' && !open && <button disabled={busy} onClick={() => setOpen(true)} style={link}>Set aside…</button>}
        {m.status === 'set_aside' && <button disabled={busy} onClick={() => act(() => api.post(`/api/v1/measuring/measurements/${m.id}/count-again`))} style={link}>Count it again</button>}
      </div>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {reasons.map(x => <button key={x} onClick={() => setReason(x)} aria-pressed={reason === x} style={{ ...btn, padding: '4px 9px', fontSize: 12, fontWeight: 500, borderColor: reason === x ? AMBER : C.border, color: reason === x ? AMBER : DIM }}>{x}</button>)}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            <input aria-label="Note" value={note} onChange={e => setNote(e.target.value)} placeholder={reason === 'Other' ? 'Why (needed with Other)' : 'A note (optional)'} style={{ ...select, flex: '1 1 200px' }} />
            <button disabled={busy || !ready} onClick={() => { setOpen(false); act(() => api.post(`/api/v1/measuring/measurements/${m.id}/set-aside`, { reason, note })); }} style={{ ...btn, opacity: ready ? 1 : 0.5 }}>Set aside</button>
            <button onClick={() => setOpen(false)} style={link}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, id, children }) {
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}><label htmlFor={id} style={{ fontSize: 12, color: DIM, fontWeight: 600 }}>{label}</label>{children}</div>;
}
function Note({ children }) {
  return <div style={{ background: SUNK, border: `1px solid ${AMBER}`, borderRadius: 9, padding: '10px 12px', fontSize: 13, color: C.text, marginBottom: 14 }}>{children}</div>;
}

const card = { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '12px 14px', boxSizing: 'border-box' };
const sect = { fontSize: 10.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.muted, margin: '2px 0 8px' };
const btn = { background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 7, padding: '6px 12px', color: C.text, fontWeight: 600, fontSize: 13, cursor: 'pointer' };
const teal = { background: TEAL, borderColor: TEAL, color: '#0b1215' };
const mini = { background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, color: DIM, width: 28, height: 28, cursor: 'pointer', fontSize: 11, padding: 0, flexShrink: 0 };
const select = { background: SUNK, border: `1px solid ${C.border}`, borderRadius: 7, padding: '5px 8px', color: C.text, fontSize: 13 };
const link = { background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 12.5, padding: 0, textDecoration: 'underline' };
